<template>
    <div>
        <strong class="text-black-50 d-block mb-3"><i>Geef extra informatie over het product</i></strong>
        <b-collapse visible :appear="isNotFirstQuestion" v-if="currentQuestion" :key="currentQuestion.question">

            <b-alert :show="hasSaveError" fade variant="danger">
                Fout bij het opslaan van het antwoord op de vraag. Probeer het opnieuw of
                <b-button variant="link" class="p-0" @click="nextQuestion">sla deze vraag over</b-button>.
            </b-alert>

            <b-alert :show="hasIsRequiredError" fade variant="warning">
                Er is iets misgegaan bij het ophalen van de controlevraag uit de PIM.
                Beantwoord daarom a.u.b. mogelijk nogmaals deze vraag.
            </b-alert>

            <b-overlay :show="isSaving" rounded="sm" spinner-variant="primary">
                <b-card no-body>
                    <b-card-header>
                        <h6 v-html="currentQuestion.question" class="m-0"/>
                    </b-card-header>
                    <b-card-body>
                        <form @submit.prevent="answerCurrentQuestion">
                            <div v-if="currentQuestion.valueType === 'boolean'" class="text-center">
                                <b-button type="submit" variant="success" @click="currentQuestion.answer = true" class="mr-4">Ja</b-button>
                                <b-button type="submit" variant="danger" @click="currentQuestion.answer = false">Nee</b-button>
                            </div>
                            <div v-else-if="currentQuestion.valueType === 'number'">
                                <b-input v-model.trim.number="currentQuestion.answer" autofocus required autocomplete="off"
                                         class="d-inline-block mr-2" style="width:150px;" type="number"
                                />
                                <b-button type="submit" variant="primary">OK</b-button>
                            </div>
                            <div v-else>
                                <b-input v-model.trim="currentQuestion.answer" autofocus required autocomplete="off" type="text"
                                         class="d-inline-block mr-2" style="width:211px;"
                                />
                                <b-button type="submit" variant="primary">OK</b-button>
                            </div>
                        </form>
                    </b-card-body>
                </b-card>
            </b-overlay>
        </b-collapse>
    </div>
</template>

<script>
import ProcessSort from "../ProcessSort.vue";
import {getLocationPropertyValue} from "@/helpers/locations";

const BATCH_MODEL_TYPE_TO_LINE_TYPE_MAPPING = {
    'shipment-batch': 'dispatch-request',
    'goods-receipt': 'goods-receipt',
}

const QUESTION_EXTRA_INFO_FOR_MULTIPLE_QUANTITIES = 'extra_info_for_multiple_quantities'

export default {
    name: "SpecialProcessProvideExtraProductInfo",

    props: {
        batchModelType: {required: true},
        productIdentifier: {required: true},
        scannedLine: {required: true},
        location: {required: true},
        hasLocationAutoCreateOnGoodsReceiptLineEnabled: {required: false},
    },

    data() {
        return {
            isRequiredValue: undefined,
            isSaving: false,
            hasSaveError: false,
            hasIsRequiredError: false,
            questions: [],
            currentQuestion: {},
            metadata: {},
            productPieceQuestionGroup: null,
            answeredQuestions: [],
        }
    },

    computed: {
        isNotFirstQuestion() {
            return !!Object.keys(this.metadata).length
        },
        hasLocationAutofillValuesEnabled() {
            return getLocationPropertyValue(this.location.location_properties, 'processProducts.providerExtraProductInfo.autofill.enabled')
        },
    },

    mounted() {
        // this.previousAnswersForLine = this.scannedLine?.metadata?.extra_processing_steps?.[0]?.find(metadata => metadata.identifier === this.getIdentifier())?.data
        this.currentQuestion = {}
        this.questions = []
        this.productPieceQuestionGroup = Date.now() // The current epoch time in milliseconds, should be unique enough ;-)
    },

    methods: {
        getIdentifier() {
            return 'provide_extra_product_info'
        },
        async isRequired(batchModelType, productIdentifier, dispatchRequest, sortingLocation, isLastScan, scannedLine) {
            return this.$http.get(`/api/rest/process-product/provide-extra-product-info/${productIdentifier}`, {
                timeout: 5 * 1000,
            })
                .catch(e => {
                    this.$alert('Fout bij het ophalen van extra vragen over het product. Deze stap wordt overgeslagen', 'danger')
                    throw e
                })
                .then(response => {
                    this.isRequiredValue = response.data.is_required

                    if (response.data.is_required_error) {
                        this.hasIsRequiredError = true
                    }

                    if (this.isRequiredValue === null) {
                        this.questions.push({
                            ...response.data.is_required_question,
                            postSaveAnswerCallback: (answer) => {
                                this.isRequiredValue = !!answer

                                // This question determines if providing extra info is required. If the answer is negative, discard all that questions. 
                                // This will let the component emit the success-event and move on 
                                if (!answer) {
                                    this.questions = []
                                }
                            }
                        })
                    }

                    if (this.scannedLine) {
                        this.questions = this.questions.concat(response.data.product_piece_questions)
                    }

                    // Check if more than 1 piece can be handled at once
                    let quantityPending = 0;
                    let drqLine = dispatchRequest?.dispatch_request_lines?.find(line => line.ean_codes.includes(productIdentifier))
                    if (!drqLine) {
                        if (scannedLine?.goods_receipt_id && scannedLine.cascaded_quantity_pending > 0) {
                            quantityPending = scannedLine.cascaded_quantity_pending
                        }
                    }
                    else {
                        quantityPending = Math.min(drqLine.cascaded_quantity_pending, scannedLine?.cascaded_quantity_pending)
                    }


                    if (this.scannedLine && (quantityPending > 1 || this.hasLocationAutoCreateOnGoodsReceiptLineEnabled)) {
                        this.questions.push({
                            attributeIdentifier: QUESTION_EXTRA_INFO_FOR_MULTIPLE_QUANTITIES,
                            valueType: "number",
                            question: (this.hasLocationAutoCreateOnGoodsReceiptLineEnabled)
                                ? `Voor hoeveel items gelden de zojuist gegeven antwoorden?`
                                : `Er zitten ${quantityPending} van dit product op deze orderregel. Voor hoeveel gelden de zojuist gegeven antwoorden?`,
                            answer: (this.hasLocationAutoCreateOnGoodsReceiptLineEnabled)
                                ? 1
                                : quantityPending,
                            preSaveAnswerCallback: async (answer) => {
                                // Failsafe for weird answers.
                                if (isNaN(answer) || answer <= 0) {
                                    return false
                                }

                                this.metadata.quantity = answer
                                if (!this.hasLocationAutoCreateOnGoodsReceiptLineEnabled && answer > quantityPending) {
                                    answer = quantityPending
                                }

                                ProcessSort.methods.setCurrentProductQuantity(answer)
                                answer-- // We already saved the answers once

                                if (answer > 5) {
                                    this.$alert(`Bezig met opslaan, dit kan even duren...`, 'info')
                                }

                                for (let i = 0; i < answer; i++) {
                                    this.productPieceQuestionGroup = Date.now()
                                    for (let k = 0; k < this.answeredQuestions.length; k++) {
                                        await this.answerQuestion(this.answeredQuestions[k])
                                    }
                                }
                            },
                        })
                    }

                    // Strange things happen when the success event is emitted while still checking if this step is required. So don't.
                    this.nextQuestion(false)

                    return this.isRequiredValue !== false // null or true should trigger the step
                })
        },
        onShow() {

        },
        nextQuestion(emitOnEmptyQuestion = true) {
            this.hasSaveError = false

            this.currentQuestion = this.questions.shift()
            if (!this.currentQuestion) {
                if (emitOnEmptyQuestion) {
                    this.$emit('success', this.metadata)
                    return
                }

                throw new Error("Expected a question but none found")
            }

            if (this.currentQuestion && !this.currentQuestion.hasOwnProperty('answer')) {
                this.currentQuestion.answer = null
            }

            this.isSaving = false

            if (this.currentQuestion.autofillValue && this.hasLocationAutofillValuesEnabled) {
                this.currentQuestion.answer = (this.currentQuestion.valueType === 'boolean')
                    ? !!parseInt(this.currentQuestion.autofillValue)
                    : this.currentQuestion.autofillValue;

                // Zonder deze timeout komt isSaving om eoa reden niet door in de template waardoor de overlay met spinner niet getoond wordt
                setTimeout(() => this.answerCurrentQuestion(), 1);
            }
        },
        async answerCurrentQuestion() {
            this.hasSaveError = false
            this.isSaving = true;

            if (typeof this.currentQuestion.preSaveAnswerCallback === 'function') {
                await this.currentQuestion.preSaveAnswerCallback(this.currentQuestion.answer)
            }

            this.answeredQuestions.push(_.clone(this.currentQuestion))
            let savePromise = this.answerQuestion(this.currentQuestion)

            await savePromise
                .finally(() => {
                    if (typeof this.currentQuestion.postSaveAnswerCallback === 'function') {
                        this.currentQuestion.postSaveAnswerCallback(this.currentQuestion.answer)
                    }

                    this.$set(this.metadata, this.currentQuestion.attributeIdentifier, this.currentQuestion.answer)
                    this.nextQuestion()
                    this.isSaving = false
                })
        },
        async answerQuestion(question) {
            return (typeof question.pimDataCollectionIdentifier !== 'undefined')
                ? this.saveAnswerToProduct(question)
                : this.saveAnswerToProductPiece(question)
        },
        async saveAnswerToProduct(question) {
            return this.$http.put(`/api/rest/process-product/provide-extra-product-info/${this.productIdentifier}/product`, {
                attribute_identifier: question.attributeIdentifier,
                value: this.convertAnswerToString(question),
            })
                .catch(e => {
                    // Do nothing on error, just continue
                    console.error(e)
                })
                .finally(() => {
                    this.hasIsRequiredError = false
                })
        },
        async saveAnswerToProductPiece(question) {
            if (question.attributeIdentifier === QUESTION_EXTRA_INFO_FOR_MULTIPLE_QUANTITIES) {
                return
            }

            return this.$http.post(`/api/rest/process-product/provide-extra-product-info/${this.productIdentifier}/product-piece`, {
                line_type: BATCH_MODEL_TYPE_TO_LINE_TYPE_MAPPING[this.batchModelType],
                line_id: this.scannedLine.id,
                group: this.productPieceQuestionGroup,
                attribute: question.attributeIdentifier,
                value: this.convertAnswerToString(question),
            })
                .catch(e => {
                    console.error(e)
                    this.hasSaveError = true
                })
        },
        convertAnswerToString(question) {
            switch (question.valueType) {
                case 'boolean':
                    return question.answer ? '1' : '0'
                default:
                    return question.answer.toString()
            }
        },
    },
}
</script>

<style scoped>

</style>
